import { useRef, useEffect, useState, useCallback } from "react";

interface AudioTrimmerProps {
  audioUrl: string;
  duration: number;
  trimStart: number;
  trimEnd: number;
  onChange: (start: number, end: number) => void;
  onSaveDefault?: (start: number, end: number) => Promise<void>;
  hasSavedDefault?: boolean;
}

export function AudioTrimmer({ audioUrl, duration, trimStart, trimEnd, onChange, onSaveDefault, hasSavedDefault }: AudioTrimmerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const waveformDataRef = useRef<Float32Array | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | 'region' | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  // For region drag: time offset from trimStart where the user grabbed
  const regionDragOffsetRef = useRef(0);
  // Suppress click after drag
  const didDragRef = useRef(false);

  const CANVAS_HEIGHT = 80;
  const HANDLE_WIDTH = 12;
  const MIN_TRIM_LENGTH = 3; // Minimum 3 seconds

  // Load and decode audio
  useEffect(() => {
    let cleanup = false;

    async function loadAudio() {
      try {
        setIsLoading(true);
        setError(null);

        // Initialize audio context
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        // Fetch and decode audio
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error('Failed to load audio');
        
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        
        if (cleanup) return;

        audioBufferRef.current = audioBuffer;
        
        // Generate waveform data by downsampling
        const samples = audioBuffer.getChannelData(0);
        const barsCount = 500; // Number of bars to display
        const samplesPerBar = Math.floor(samples.length / barsCount);
        const waveformData = new Float32Array(barsCount);

        for (let i = 0; i < barsCount; i++) {
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, samples.length);
          let max = 0;
          
          for (let j = start; j < end; j++) {
            max = Math.max(max, Math.abs(samples[j]));
          }
          
          waveformData[i] = max;
        }

        waveformDataRef.current = waveformData;
        setIsLoading(false);
      } catch (err) {
        if (!cleanup) {
          setError(err instanceof Error ? err.message : 'Failed to load audio');
          setIsLoading(false);
        }
      }
    }

    loadAudio();

    return () => {
      cleanup = true;
    };
  }, [audioUrl]);

  // Draw waveform — fully self-contained, handles its own DPR scaling
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const waveformData = waveformDataRef.current;
    if (!canvas || !waveformData) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return; // Not laid out yet

    const dpr = window.devicePixelRatio || 1;
    const physW = Math.round(rect.width * dpr);
    const physH = Math.round(CANVAS_HEIGHT * dpr);

    // Resize backing store if needed (resets transform)
    if (canvas.width !== physW || canvas.height !== physH) {
      canvas.width = physW;
      canvas.height = physH;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${CANVAS_HEIGHT}px`;
    }

    const ctx = canvas.getContext('2d')!;
    // Always reset to clean DPR scale — no accumulated transforms
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const width = rect.width;   // CSS pixels
    const height = CANVAS_HEIGHT; // CSS pixels

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Calculate positions
    const startX = (trimStart / duration) * width;
    const endX = (trimEnd / duration) * width;
    const barWidth = width / waveformData.length;

    // Draw waveform bars
    for (let i = 0; i < waveformData.length; i++) {
      const x = i * barWidth;
      const barHeight = waveformData[i] * height * 0.8;
      const y = (height - barHeight) / 2;

      // Different colors for selected/unselected regions
      const isSelected = x >= startX && x <= endX;
      ctx.fillStyle = isSelected ? '#6366f1' : '#374151'; // indigo-500 : slate-700

      ctx.fillRect(x, y, Math.max(1, barWidth - 0.5), barHeight);
    }

    // Draw selected region overlay
    ctx.fillStyle = 'rgba(99, 102, 241, 0.1)'; // indigo with opacity
    ctx.fillRect(startX, 0, endX - startX, height);

    // Draw trim handles
    const handleHeight = height;
    
    // Start handle
    ctx.fillStyle = '#6366f1'; // indigo-500
    ctx.fillRect(startX - HANDLE_WIDTH / 2, 0, HANDLE_WIDTH, handleHeight);
    
    // End handle  
    ctx.fillRect(endX - HANDLE_WIDTH / 2, 0, HANDLE_WIDTH, handleHeight);

    // Draw handle grab lines
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const offset = i * 3 - 3;
      // Start handle lines
      ctx.beginPath();
      ctx.moveTo(startX + offset, height * 0.3);
      ctx.lineTo(startX + offset, height * 0.7);
      ctx.stroke();
      // End handle lines
      ctx.beginPath();
      ctx.moveTo(endX + offset, height * 0.3);
      ctx.lineTo(endX + offset, height * 0.7);
      ctx.stroke();
    }
  }, [duration, trimStart, trimEnd]);

  // Update canvas when props change
  useEffect(() => {
    if (!isLoading && !error) {
      requestAnimationFrame(drawWaveform);
    }
  }, [drawWaveform, isLoading, error]);

  // Handle canvas resize — drawWaveform handles its own DPR setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      drawWaveform();
    });
    observer.observe(canvas);

    // Initial draw
    drawWaveform();

    return () => observer.disconnect();
  }, [drawWaveform]);

  // Get handle or region at position
  const getHandleAtPosition = (x: number): 'start' | 'end' | 'region' | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const canvasX = (x - rect.left) / rect.width * canvas.width / (window.devicePixelRatio || 1);
    const width = canvas.width / (window.devicePixelRatio || 1);

    const startX = (trimStart / duration) * width;
    const endX = (trimEnd / duration) * width;

    // Handles take priority over region — use slightly larger hit zone than visual for easier grabbing
    const HIT_ZONE = HANDLE_WIDTH + 4;
    if (Math.abs(canvasX - startX) <= HIT_ZONE / 2) return 'start';
    if (Math.abs(canvasX - endX) <= HIT_ZONE / 2) return 'end';
    // Check if inside the selected region (between handles)
    if (canvasX > startX + HANDLE_WIDTH / 2 && canvasX < endX - HANDLE_WIDTH / 2) return 'region';
    return null;
  };

  // Convert position to time
  const positionToTime = (x: number): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;

    const rect = canvas.getBoundingClientRect();
    const normalizedX = (x - rect.left) / rect.width;
    return Math.max(0, Math.min(duration, normalizedX * duration));
  };

  // Update cursor based on what's under the pointer
  const updateCursor = useCallback((clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const target = getHandleAtPosition(clientX);
    if (target === 'start' || target === 'end') {
      canvas.style.cursor = 'ew-resize';
    } else if (target === 'region') {
      canvas.style.cursor = isDragging === 'region' ? 'grabbing' : 'grab';
    } else {
      canvas.style.cursor = 'pointer';
    }
  }, [getHandleAtPosition, isDragging]);

  // Handle mouse/touch events
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    didDragRef.current = false;
    const target = getHandleAtPosition(e.clientX);
    if (!target) return;

    if (target === 'region') {
      // Store offset from trimStart where the grab happened
      regionDragOffsetRef.current = positionToTime(e.clientX) - trimStart;
    }
    setIsDragging(target);
    canvasRef.current?.setPointerCapture(e.pointerId);
    if (target === 'region' && canvasRef.current) {
      canvasRef.current.style.cursor = 'grabbing';
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) {
      updateCursor(e.clientX);
      return;
    }
    
    e.preventDefault();
    didDragRef.current = true;
    const newTime = positionToTime(e.clientX);
    
    if (isDragging === 'start') {
      const newStart = Math.max(0, Math.min(newTime, trimEnd - MIN_TRIM_LENGTH));
      onChange(newStart, trimEnd);
    } else if (isDragging === 'end') {
      const newEnd = Math.min(duration, Math.max(newTime, trimStart + MIN_TRIM_LENGTH));
      onChange(trimStart, newEnd);
    } else if (isDragging === 'region') {
      // Slide the whole window, keeping trim length constant
      const trimLength = trimEnd - trimStart;
      let newStart = newTime - regionDragOffsetRef.current;
      // Clamp to bounds
      newStart = Math.max(0, Math.min(newStart, duration - trimLength));
      onChange(newStart, newStart + trimLength);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(null);
    canvasRef.current?.releasePointerCapture(e.pointerId);
    updateCursor(e.clientX);
  };

  // Handle canvas click — only for clicking outside the region/handles to jump a handle
  const handleCanvasClick = (e: React.MouseEvent) => {
    // Suppress click if we just finished dragging
    if (didDragRef.current) return;
    
    const target = getHandleAtPosition(e.clientX);
    if (target) return; // Handles and region are managed by drag events

    const clickTime = positionToTime(e.clientX);
    
    // Move the closest handle to the click position
    const distanceToStart = Math.abs(clickTime - trimStart);
    const distanceToEnd = Math.abs(clickTime - trimEnd);
    
    if (distanceToStart < distanceToEnd) {
      const newStart = Math.max(0, Math.min(clickTime, trimEnd - MIN_TRIM_LENGTH));
      onChange(newStart, trimEnd);
    } else {
      const newEnd = Math.min(duration, Math.max(clickTime, trimStart + MIN_TRIM_LENGTH));
      onChange(trimStart, newEnd);
    }
  };

  // Play preview
  const handlePlayPreview = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.currentTime = trimStart;
      audio.play();
      setIsPlaying(true);
    }
  };

  // Handle audio events
  const handleAudioTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !isPlaying) return;

    if (audio.currentTime >= trimEnd) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  const trimmedDuration = trimEnd - trimStart;

  return (
    <div className="space-y-3">
      {/* Header with duration info */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-400">Audio Trim</span>
        <span className="text-indigo-400 font-medium">
          Using {formatTime(trimmedDuration)} of {formatTime(duration)}
        </span>
      </div>

      {/* Waveform Canvas */}
      <div className="card p-3">
        {isLoading && (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent" />
          </div>
        )}
        
        {error && (
          <div className="flex items-center justify-center h-20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="space-y-3">
            <canvas
              ref={canvasRef}
              className="w-full touch-none"
              style={{ height: CANVAS_HEIGHT }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onClick={handleCanvasClick}
            />

            {/* Time markers */}
            <div className="flex justify-between text-xs text-slate-500">
              <span>{formatTime(trimStart)}</span>
              <span>{formatTime(trimEnd)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlayPreview}
          disabled={isLoading || error !== null}
          className="btn-ghost text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isPlaying ? "⏸" : "▶"} Preview Trim
        </button>

        {onSaveDefault && (
          <button
            onClick={async () => {
              setSaveState('saving');
              try {
                await onSaveDefault(trimStart, trimEnd);
                setSaveState('saved');
                setTimeout(() => setSaveState('idle'), 2000);
              } catch {
                setSaveState('idle');
              }
            }}
            disabled={saveState === 'saving'}
            className={`text-sm px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors ${
              saveState === 'saved'
                ? 'bg-amber-500/20 text-amber-300'
                : hasSavedDefault
                  ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                  : 'btn-ghost text-slate-400 hover:text-amber-400'
            }`}
            title="Save this trim as default for this voice"
          >
            {saveState === 'saving' ? '⏳' : saveState === 'saved' ? '⭐' : hasSavedDefault ? '⭐' : '☆'}
            {saveState === 'saved' ? 'Saved!' : 'Set Default'}
          </button>
        )}
        
        <div className="text-xs text-slate-500 ml-auto">
          Drag handles to resize • Drag center to slide
        </div>
      </div>

      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={handleAudioEnded}
        onPause={() => setIsPlaying(false)}
        preload="metadata"
      />
    </div>
  );
}